import { HttpClient } from '@angular/common/http';
import { Component, inject, Input, OnChanges } from '@angular/core';
import { DateUtilService } from 'gung-common';
import { first, Observable } from 'rxjs';
import { EdiOrderMessageRelations } from '../../../models/edi';
import { EdiDispatchAdvicesActionConfigService } from '../../../services/edi-dispatch-advices-action-config/edi-dispatch-advices-action-config.service';
import { EdiInvoicesActionConfigService } from '../../../services/edi-invoices-action-config/edi-invoices-action-config.service';
import { EdiRespondeOrderResponseActionConfigService } from '../../../services/edi-order-response-action-config/edi-order-response-action-config.service';

export type EdiRelatedMessageKind = 'ordrsp' | 'desadv' | 'invoice';

interface RelatedKindSettings {
  title: string;
  readyTitle: string;
  route: string;
}

@Component({
  selector: 'lib-edi-related-messages',
  templateUrl: './edi-related-messages.component.html',
  styleUrls: ['../edi-document-detail.shared.css'],
  standalone: false
})
export class EdiRelatedMessagesComponent implements OnChanges {
  @Input() kind: EdiRelatedMessageKind;
  @Input() relations: EdiOrderMessageRelations;
  @Input() ediConfigId?: string;
  @Input() currentMessageId?: string;
  // The EDI order page already loads these for customer templates; passing them in avoids loading them twice
  @Input() messages?: { [erpId: string]: any[] };
  @Input() toGenerate?: string[];

  protected http = inject(HttpClient);
  public dateUtilService = inject(DateUtilService);
  public orderResponseActionConfig = inject(EdiRespondeOrderResponseActionConfigService);
  public despatchActionConfig = inject(EdiDispatchAdvicesActionConfigService);
  public invoiceActionConfig = inject(EdiInvoicesActionConfigService);

  loadedMessages: { [erpId: string]: any[] } = {};
  loadedToGenerate: string[] = [];

  readonly settings: { [kind in EdiRelatedMessageKind]: RelatedKindSettings } = {
    ordrsp: { title: 'ORDER_RESPONSES', readyTitle: 'ORDERS_IDS_READY_TO_GENERATE', route: '/admin/edi/order-responses/' },
    desadv: { title: 'DESPATCHES', readyTitle: 'DESPATCHES_IDS_READY_TO_GENERATE', route: '/admin/edi/despatch-advices/' },
    invoice: { title: 'INVOICES', readyTitle: 'INVOICES_IDS_READY_TO_GENERATE', route: '/admin/edi/invoices/' }
  };

  get groupIds(): string[] {
    if (this.kind === 'ordrsp') {
      return this.relations?.erpOrdrspIds?.length ? this.relations.erpOrdrspIds : this.relations?.erpOrderIds || [];
    }
    return (this.kind === 'desadv' ? this.relations?.erpDesadvIds : this.relations?.erpInvoiceIds) || [];
  }

  get shownMessages(): { [erpId: string]: any[] } {
    return this.messages || this.loadedMessages;
  }

  get shownToGenerate(): string[] {
    return this.toGenerate || this.loadedToGenerate;
  }

  get actionConfig(): any {
    return this.kind === 'ordrsp'
      ? this.orderResponseActionConfig
      : this.kind === 'desadv'
        ? this.despatchActionConfig
        : this.invoiceActionConfig;
  }

  get messageCount(): number {
    return this.groupIds.reduce((count, id) => count + (this.shownMessages[id]?.length || 0), 0);
  }

  ngOnChanges(): void {
    this.loadedMessages = {};
    this.loadedToGenerate = [];
    if (!this.relations || this.messages) {
      return;
    }
    for (const erpId of this.groupIds) {
      this.loadMessages(erpId)
        .pipe(first())
        .subscribe(messages => {
          if (messages?.length > 0) {
            this.loadedMessages[erpId] = messages;
          } else {
            this.loadedToGenerate.push(erpId);
          }
        });
    }
  }

  protected loadMessages(erpId: string): Observable<any[]> {
    const id = encodeURIComponent(erpId);
    if (this.kind === 'ordrsp') {
      return this.http.get<any[]>('json/edi/v2/order-response/messages/by-order-id?orderId=' + id);
    }
    if (this.kind === 'desadv') {
      return this.http.get<any[]>('json/edi/v2/despatch/messages/by-despatch-id?despatchId=' + id);
    }
    return this.http.get<any[]>('json/edi/v2/invoice/messages/by-invoice-id?invoiceId=' + id);
  }

  generate(erpId: string): void {
    const id = encodeURIComponent(erpId);
    const url =
      this.kind === 'ordrsp'
        ? 'json/edi/v2/actuator/ordrsp/generate-by-order-id-get?orderId=' + id
        : this.kind === 'desadv'
          ? 'json/edi/v2/actuator/desadv/generate-by-despatch-system-id-get?despatchSystemId=' + id
          : 'json/edi/v2/actuator/invoice/generate-by-invoice-id-get?invoiceId=' + id + '&ediConfig=' + this.ediConfigId;
    this.http.get(url).pipe(first()).subscribe();
  }

  messageDate(message: any): Date | string {
    const references = message?.documentReferences;
    const date =
      this.kind === 'ordrsp'
        ? references?.orderResponse?.date
        : this.kind === 'desadv'
          ? references?.despatchAdvice?.date
          : references?.invoice?.date;
    return date || message?.timestamp;
  }
}
